/**
 * Settings Overview Page
 *
 * Shows all settings groups as clickable section cards, with each group's
 * items listed beneath. Groups link to their own dedicated index pages.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, SlidersHorizontal } from 'lucide-react';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { isModuleEnabled, useWorkspaceModules } from '../../../lib/workspaceModulesService';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  PERSONAL_GROUP,
  WORKSPACE_GROUPS,
  filterVisibleSettingsItems,
  type SettingsAccessContext,
} from './settingsConfig';
import type { SettingsGroup, SettingsItem } from './settingsConfig';
import {
  settingsGroupDesc,
  settingsGroupHeaderLink,
  settingsGroupTitle,
  settingsLinkCard,
  settingsLinkCardDesc,
  settingsLinkCardIcon,
  settingsLinkCardTitle,
} from './settingsUi';

function ItemCard({ item }: { item: SettingsItem }) {
  const Icon = item.icon;
  return (
    <Link to={item.href} className={settingsLinkCard}>
      <div className={settingsLinkCardIcon}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className={settingsLinkCardTitle}>{item.name}</h4>
        <p className={settingsLinkCardDesc}>{item.description}</p>
      </div>
    </Link>
  );
}

function GroupSection({ group, visible }: { group: SettingsGroup; visible: boolean }) {
  if (!visible) return null;
  const GroupIcon = group.icon;

  return (
    <section>
      <Link to={group.indexHref} className={settingsGroupHeaderLink}>
        <div className="flex h-6 w-6 items-center justify-center bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] text-[var(--text-ter)] dark:text-[var(--text-sec)] group-hover:bg-primary-50 dark:group-hover:bg-[var(--active-tint)] group-hover:text-primary-600 dark:group-hover:text-[var(--text-pri)]">
          <GroupIcon className="h-3.5 w-3.5" />
        </div>
        <h2 className={settingsGroupTitle}>{group.title}</h2>
        <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </Link>
      <p className={settingsGroupDesc}>{group.description}</p>
      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        {group.items.map((item) => (
          <ItemCard key={item.href} item={item} />
        ))}
      </div>
    </section>
  );
}

export default function SettingsIndex() {
  const currentRole = useUserStore((state) => state.currentRole);
  const canAny = useUserStore((state) => state.canAny);
  const workspaceFeatures = useWorkspaceModules();
  const isPlatformAdmin = currentRole === 'platform-admin' || currentRole === 'system-admin';
  const ctx: SettingsAccessContext = useMemo(
    () => ({
      canAny,
      isPlatformAdmin,
      isModuleEnabled: (moduleKey: string) => isModuleEnabled(workspaceFeatures, moduleKey),
    }),
    [canAny, isPlatformAdmin, workspaceFeatures],
  );

  const personalItems = filterVisibleSettingsItems(PERSONAL_GROUP.items, ctx);
  const workspaceGroupsFiltered = WORKSPACE_GROUPS.map((group) => ({
    group,
    items: filterVisibleSettingsItems(group.items, ctx),
    allowed: true,
  }));

  return (
    <div className="space-y-10 pp-settings">
      <PageHeader
        icon={<SlidersHorizontal className="h-4 w-4 text-[var(--text-sec)] dark:text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Settings"
        subtitle="Workspace configuration and personal preferences"
      />

      <GroupSection
        group={{ ...PERSONAL_GROUP, items: personalItems }}
        visible={personalItems.length > 0}
      />

      {workspaceGroupsFiltered.map(({ group, items, allowed }) => (
        <GroupSection
          key={group.key}
          group={{ ...group, items }}
          visible={allowed && items.length > 0}
        />
      ))}
    </div>
  );
}
